import type { Map as MapType } from "mapbox-gl";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AvatarMenu,
  LanguageSettings,
  Map,
  Markdown,
  PageNotFoundTemplate,
  useIsMobile,
} from "senf-atomic-design-system";
import TextToSpeech from "senf-atomic-design-system/src/components/atoms/textToSpeech/TextToSpeech";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import ParticipationModule, {
  type ParticipationModuleProps,
} from "senf-atomic-design-system/src/components/special/modalContents/ParticipationModule";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import {
  fixFalslyTranslatedMarkdown,
  getPhaseOfDateRange,
  setGlobalTextSize,
  stripMarkdown,
  useAuthContext,
  useGetFireStoreSizes,
  useGlobalState,
} from "senf-shared";
import type { Project } from "senf-shared/types";
import { useGetTranslations } from "senf-shared/utils/hooks";
import i18n from "senf-shared/utils/i18n";
import { tv } from "tailwind-variants";
import LanguageButton from "../../../components/molecules/languageSettings/LanguageButton";
import { useImageMetaData } from "../../../hooks/useImageMetaData";
import MapViewEditor from "../../atoms/map/MapViewEditor";
import Dropdown from "../../core/dropdown/Dropdown";
import Seperator from "../../core/seperator/Seperator";
import AccessibilitySettings from "../accessibilitySettings/AccessibilitySettings";
import { CopyrightIcon } from "../copyright/CopyrightIcon";
import {
  ButtonSection,
  CalendarSection,
  FAQSection,
  FileSection,
  ImageSection,
  LogoSection,
  MapSection,
  TextSection,
  TimelineSection,
} from "./InfoPageComponents";

export const useGetImageRatio = (url: string | undefined) => {
  const [ratio, setRatio] = useState<number>(1);

  useEffect(() => {
    const img = new Image();
    if (url) {
      img.src = url;
      img.onload = () => {
        setRatio(img.width / img.height);
      };
    }
  }, [url]);

  return ratio;
};

export const InfoPageStyles = tv({
  slots: {
    Outer:
      "@container z-0 flex h-full w-full flex-col items-start overflow-y-auto overflow-x-hidden whitespace-pre-wrap bg-white [&.desktop]:flex-row-reverse [&.desktop]:justify-between",
    MainContainer: "z-50 w-full [.desktop_&]:w-1/2 bg-white",
    MainContent: "mx-auto flex max-w-2xl flex-col p-8",
    ImgContainer:
      "sticky top-0 flex h-2/3 w-full items-center justify-center [.desktop_&]:h-full [.desktop_&]:w-1/2",
    ImgWrapper: "relative",
    Img: "w-full h-full object-cover",
  },
  variants: {
    isMobile: {
      false: {
        Outer: "desktop",
        MainContent: "gap-20 pt-12",
        MainContainer: "mx-auto",
      },
      true: {
        MainContent: "gap-16 p-5",
        MainContainer: "rounded-3xl",
      },
    },
    full: {
      false: {
        ImgContainer:
          "[.desktop_&]:p-10 w-full h-2/3 [.desktop_&]:h-full box-border flex items-center justify-center",
        ImgWrapper: "max-w-full max-h-full w-full [.desktop_&]:w-auto",
        Img: "[.desktop_&]:rounded-xl",
      },
      true: {
        ImgContainer: "h-2/3 w-full",
        ImgWrapper: "w-full h-full",
        Img: "w-full h-full object-cover",
      },
    },
  },
});

export const PageFooter = ({ project }: { project: Project }) => {
  const { t } = useTranslation();
  const projectId = useMemo(() => project?.projectId, [project]);
  if (!projectId) return null;
  const items = useMemo(
    () => [
      {
        label: "global:imprint",
        href: `/projects/${projectId}/imprint`,
      },
      {
        label: "global:termsAndConditions",
        href: `/projects/${projectId}/terms`,
      },
      {
        label: "global:dataProtection",
        href: `/projects/${projectId}/privacyPolicy`,
      },
      {
        label: "accessibilityStatement:title",
        href: `/projects/${projectId}/accessibilityStatement`,
      },
    ],
    [projectId],
  );
  return (
    <>
      {items.map((item) => (
        <a
          className="body-md text-text-subdued text-start hover:underline focus:outline-focused-default outline-offset-4 rounded"
          target="_blank"
          href={item.href}
          rel="noreferrer"
          key={item.label}
        >
          {t(item.label)}
        </a>
      ))}
    </>
  );
};
interface InfoPageProps {
  project: Project;
  handle: ParticipationModuleProps["handle"];
  variant: "mainApp" | "dashboard";
  className?: string;
  isMobile?: boolean;
  preview?: "mobile" | "tablet" | "desktop";
}

const InfoPage = ({
  variant,
  project,
  handle,
  className,
  preview,
  isMobile: isMobileProp,
}: InfoPageProps) => {
  const { t } = useTranslation();
  const [accessibility] = useGlobalState().accessibilityState;
  const [translation, setTranslation] = useGlobalState().translationsState;
  const { fetchTranslations } = useGetTranslations(project);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: project?.image ? [project.image] : [],
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  const isMobileFallback = useIsMobile();
  const isMobile = isMobileProp ?? isMobileFallback;
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const imageRatio = useGetImageRatio(project?.image?.url);
  const { Outer, MainContainer, MainContent, ImgContainer, ImgWrapper, Img } =
    InfoPageStyles({
      isMobile,
      full: !project.image?.keepAspectRatio || isMobile,
    });
  const imageProps = useGetFireStoreSizes(project?.image?.url);
  const [statefulMap, setStatefulMap] = useState<MapType>();

  const mapSections = useMemo(() => {
    return project?.content?.filter((section) => section.type === "map");
  }, [project?.content]);

  // Track the first map section to know when to show the initial image
  const firstMapSection = useMemo(() => {
    if (!mapSections || mapSections.length === 0) return null;
    return mapSections[0];
  }, [mapSections]);

  useEffect(() => {
    const sections = document.querySelectorAll("[section-id]");
    const scrollContainer = document.querySelector("#root .overflow-y-auto");
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the last map section entry that is currently intersecting the center third
        const intersectingMapEntry = entries
          .slice() // Create a copy before reversing
          .reverse() // Check from bottom-most / last triggered
          .find((entry) => {
            const sectionId = entry.target?.getAttribute("section-id");
            // Check if it's a map section and if it's intersecting
            return (
              mapSections?.some(
                (mapSection) =>
                  mapSection.uid === sectionId || "main" === sectionId,
              ) && entry.isIntersecting
            );
          });

        console.log(sections);
        console.log(intersectingMapEntry);
        if (intersectingMapEntry) {
          const sectionId =
            intersectingMapEntry.target?.getAttribute("section-id");
          // Set the current section only if a map section is intersecting the center
          console.log(sectionId);
          setCurrentSection((sectionId !== "main" && sectionId) || null);
        }
        // Note: We are not explicitly setting to null here if nothing intersects.
        // The scroll listener handles setting to null at the top.
        // If a map section leaves the center, currentSection retains the last value
        // until another map section enters or the user scrolls to the top.
      },
      {
        rootMargin: "-8% 0px -78% 0px", // Target the center third of the screen
        threshold: 0.01, // Trigger as soon as any part enters the rootMargin area
      },
    );

    for (const section of sections) {
      observer.observe(section);
    }

    // Create a new observer to detect when we're before the first map section
    const beforeMapObserver = new IntersectionObserver(
      (entries) => {
        // Check if we're intersecting with a section before the first map section
        const isBeforeFirstMap = entries.some((entry) => {
          const sectionId = entry.target?.getAttribute("section-id");
          // If we're at the main section or any section before the first map section
          return (
            sectionId === "main" ||
            (firstMapSection &&
              project?.content &&
              project.content.findIndex(
                (section) => section.uid === sectionId,
              ) <
                project.content.findIndex(
                  (section) => section.uid === firstMapSection.uid,
                ))
          );
        });

        if (isBeforeFirstMap) {
          setCurrentSection(null);
        }
      },
      {
        rootMargin: "-48% 0px -48% 0px",
        threshold: 0.01,
      },
    );

    // Observe all sections for the before-map detection
    for (const section of sections) {
      beforeMapObserver.observe(section);
    }

    const handleScroll = (event: Event) => {
      if ((event.target as HTMLElement)?.scrollTop === 0) {
        setCurrentSection(null);
        return;
      }
    };

    scrollContainer?.addEventListener("scroll", handleScroll);

    return () => {
      for (const section of sections) {
        observer.unobserve(section);
        beforeMapObserver.unobserve(section);
      }
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [mapSections, firstMapSection, project?.content]);
  const currentMapSection = useMemo(() => {
    const section = mapSections?.find((e) => e.uid === currentSection);

    // Define mapSettings type inline
    type MapSettings = {
      center: [number, number];
      pitch: number;
      bearing: number;
      zoom?: number;
      height?: number;
    };

    // Default mapSettings
    // const defaultMapSettings: MapSettings = {
    //   center: [0, 0],
    //   pitch: 0,
    //   bearing: 0,
    //   zoom: 10
    // };

    if (section) {
      // Get mapData from the project's maps array based on the mapId
      const mapData =
        section.mapId && section.mapId !== ""
          ? project?.maps?.find((map) => map.mapId === section.mapId)
          : undefined;

      return {
        // mapSettings: defaultMapSettings,
        mapData,
        ...section,
      };
    }

    return {
      type: "map",
      uid: "",
      map: {} as MapType,
      mapData: undefined,
      // mapSettings: defaultMapSettings
    };
  }, [mapSections, currentSection]);

  // @TODO remove firestore/lite and use firestore realtime listener if needed
  /*   useEffect(() => {
    const projectDocRef = doc(
      db,
      `organizations/${organizationId}/projects/${projectId}`
    );

    const unsubscribe = onSnapshot(
      projectDocRef,
      (doc) => {
        if (doc.exists()) {
          setLogoUrlsBySize(doc.project().logoUrlsBySize || []);
        }
      },
      (error) => {
        console.error("Error listening to project document:", error);
      }
    );

    return () => unsubscribe();
  }, [organizationId, projectId]); */

  const sectionsMap = {
    text: TextSection,
    faq: FAQSection,
    image: ImageSection,
    logo: LogoSection,
    map: MapSection,
    video: ImageSection,
    file: FileSection,
    button: ButtonSection,
    calendar: CalendarSection,
    timeline: TimelineSection,
  };

  // const [marginTop, setMarginTop] = useState(-40);

  // useEffect(() => {
  //   const adjustMargin = () => {
  //     const m = isMobile
  //       ? -40
  //       : Math.max(
  //           Math.min(
  //             window.innerHeight * 0.38 -
  //               172 -
  //               (project?.brief?.length || 0) / 3 -
  //               (project?.title?.length || 0) / 3,
  //             224
  //           ),
  //           0
  //         );
  //     setMarginTop(m);
  //   };
  //   window.addEventListener("resize", adjustMargin);
  //   adjustMargin();
  //   return () => window.removeEventListener("resize", adjustMargin);
  // }, []);

  const publicDialogMaps = project?.ideaMaps?.filter((ideaMap) => {
    const ideaMapPhase = getPhaseOfDateRange(
      ideaMap.startDate,
      ideaMap.endDate,
    );
    return (
      ideaMap?.settings?.publicAccess !== false &&
      (ideaMapPhase === "while" || ideaMapPhase === "after")
    );
  });

  const altText = useMemo(() => {
    return (
      project.image?.alt ||
      (project.image?.url &&
        imageMetadata[project.image?.url]?.customMetadata?.alt)
    );
  }, [project.image?.alt, imageMetadata, project.image?.url]);

  const textToSpeechContent = stripMarkdown(
    `${translation?.[project?.title] || project?.title}. ${
      translation?.[project?.brief] || project?.brief
    } ${project?.content
      ?.flatMap((content) => [
        "title" in content && (translation?.[content.title] || content.title),
        ".",
        "body" in content && (translation?.[content.body] || content.body),
        ".",
        ...(("faq" in content &&
          content.faq?.flatMap((faq) => [
            translation?.[faq.question] || faq.question,
            ".",
            translation?.[faq.answer] || faq.answer,
            ".",
          ])) ||
          []),
      ])
      .filter(Boolean)
      .join(" ")}`,
  );

  return (
    <>
      {project?.projectId &&
      !project?.title &&
      !project?.brief &&
      !project?.content?.length ? (
        <PageNotFoundTemplate />
      ) : (
        <div className={cn(Outer(), className)}>
          {/* <div
            className={cn(
              "fixed top-5 z-10 ml-auto flex gap-2",
              document.dir === "rtl" ? "left-5" : "right-5",
            )}
          >
            {variant === "mainApp" ? (
              <AvatarMenu
                navigate={navigate}
                handle={handle}
                user={user}
                variant="infoPage"
                project={project}
              />
            ) : null}
          </div> */}

          {isMobile && (
            <header
              className={cn(
                "absolute  top-5 z-10 flex gap-2",
                document.dir === "rtl" ? "left-5" : "right-5",
              )}
            >
              {project?.settings?.accessibility ? (
                <AccessibilitySettings project={project} />
              ) : null}

              {project?.settings?.languages?.length ? (
                <LanguageButton project={project} />
              ) : null}
            </header>
          )}
          {!isMobile && mapSections?.length ? (
            <div
              className={cn(
                "w-1/2 h-full absolute",
                currentMapSection
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none",
              )}
            >
              <MapViewEditor
                mapSettings={currentMapSection?.mapSettings}
                mapData={currentMapSection?.mapData}
                resizeDependencies={[isMobile.toString(), preview]}
                flyToDependencies={[currentSection || ""]}
                mode="display"
                statefulMap={statefulMap}
                setStatefulMap={setStatefulMap}
                shouldFlyTo={currentMapSection?.mapSettings}
              />
            </div>
          ) : null}
          {imageProps.src ? (
            <aside
              className={ImgContainer({
                className:
                  mapSections.find((e) => e.uid === currentSection) &&
                  "opacity-0 pointer-events-none",
              })}
            >
              <div
                className={ImgWrapper()}
                style={{
                  aspectRatio: imageRatio,
                }}
              >
                <img
                  {...imageProps}
                  className={Img()}
                  alt={altText || t("global:image")}
                  aria-describedby={`figcaption-${project.image?.url}`}
                />
                {project.image?.copyright && (
                  <CopyrightIcon
                    copyright={project.image.copyright}
                    captionId={`figcaption-${project.image.url}`}
                    className={cn(
                      !project.image.keepAspectRatio && !isMobile && "m-5",
                    )}
                  />
                )}
              </div>
            </aside>
          ) : null}
          <div className={MainContainer()}>
            <main className={MainContent()}>
              <section
                className="flex flex-col gap-8"
                section-id="main"
                aria-label={t("global:summary")}
              >
                {/* <Button
                  text="Open Cookie Configurator"
                  onClick={() => openModal(<div >hi <Button text="open another modal" onClick={() => openModal(<div>another modal</div>)} /></div>)}
                /> */}
                {isMobile && accessibility ? (
                  <div className="-mb-6">
                    <TextToSpeech
                      id="projectInfoPage:ttsPlayClick"
                      text={textToSpeechContent}
                    />
                  </div>
                ) : null}

                {!isMobile && (
                  <header
                    className=" mb-2 flex items-center gap-2"
                    // style={{ marginTop }}
                  >
                    {project?.settings?.languages?.length ? (
                      <LanguageButton project={project} />
                    ) : null}
                    {project?.settings?.accessibility ? (
                      <AccessibilitySettings project={project} />
                    ) : null}

                    {accessibility && (
                      <TextToSpeech
                        id="projectInfoPage:ttsPlayClick"
                        text={textToSpeechContent}
                      />
                    )}
                  </header>
                )}
                <h1
                  className={cn(
                    "break-normal hyphens-auto [text-wrap:balance]",
                    isMobile ? "heading-3xl mt-3" : "heading-5xl",
                  )}
                >
                  {translation?.[project?.title] || project?.title}
                </h1>
                <p className="body-md">
                  <Markdown>
                    {fixFalslyTranslatedMarkdown(
                      translation?.[project?.brief] || project?.brief,
                    )}
                  </Markdown>
                </p>

                <nav className="flex w-full flex-col gap-2 @lg:flex-row">
                  <div className="w-full">
                    <ParticipationModule
                      project={project}
                      handle={handle}
                      variant="info"
                      handleNavigate={() =>
                        handle?.openMap(publicDialogMaps?.[0])
                      }
                    />
                  </div>
                  {publicDialogMaps?.length === 1 ? (
                    <Button
                      classNameWrapper="w-full @lg:w-1/2"
                      className="w-full"
                      variant="outline"
                      text={t("global:dialogMap")}
                      width={isMobile ? "max" : undefined}
                      size={isMobile ? "lg" : "xl"}
                      onClick={() => handle?.openMap(publicDialogMaps?.[0])}
                    />
                  ) : (publicDialogMaps?.length || 0) > 1 ? (
                    <Dropdown
                      asChild
                      variant="outline"
                      text={t("global:dialogMaps")}
                      width={isMobile ? "max" : undefined}
                      size="xl"
                      trailingIcon={false}
                      items={publicDialogMaps?.map((publicDialogMap) => {
                        return {
                          text: publicDialogMap?.title,
                          onClick: () => handle?.openMap(publicDialogMap),
                        };
                      })}
                    />
                  ) : null}
                </nav>
              </section>
              {project?.content?.map((section) => {
                const Component = sectionsMap[section.type || "text"];
                if (!Component) return null;
                return (
                  <Component
                    key={section.uid}
                    section={section}
                    project={project}
                  />
                );
              })}
              <br /> <br /> <br />
              <footer className={isMobile ? "pb-20" : "pb-8"}>
                <Seperator className="mb-4" />
                <div className="flex justify-between gap-5 items-center flex-wrap">
                  <div className="flex gap-3">
                    <PageFooter project={project} />
                  </div>

                  {user?.userId &&
                  (!user?.isAnonymous ||
                    import.meta.env.VITE_APP_MODE !== "production") ? (
                    <Button
                      text={t("auth:logout")}
                      leadingIcon="logout"
                      onClick={handle?.logout}
                    />
                  ) : (
                    <Button
                      text={t("auth:profile.registerOrLogin")}
                      leadingIcon="user-fill"
                      onClick={handle?.loginInfoPage}
                    />
                  )}
                </div>
              </footer>
              {/* <footer role="contentinfo">
                <p>&copy; 2024 St. Pauli. All rights reserved.</p>
                <p>Powered by Senf.app</p>
              </footer> */}
            </main>
          </div>
        </div>
      )}
    </>
  );
};

export default InfoPage;
